import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auditLogService } from '../../../api/services/auditLogService';
import {
  Shield, Eye, Download, Filter,
  Search, RefreshCw, AlertTriangle, CheckCircle, XCircle,
  FileText, Database, Lock, Edit, Activity,
  TrendingUp, BarChart3, PieChart
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import { formatIndianNumber } from '../../../utils/amountFormatter';

const AuditLogs = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSeverity, setSelectedSeverity] = useState('all');
  const [selectedUser, setSelectedUser] = useState('all');
  const [dateRange, setDateRange] = useState('7days');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewType, setViewType] = useState('logs');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [serverLogs, setServerLogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // Mock data - would come from API
  const auditMetrics = {
    totalEvents: { current: 2847, previous: 2654, target: 3000 },
    criticalEvents: { current: 12, previous: 18, target: 10 },
    failedLogins: { current: 23, previous: 31, target: 20 },
    dataChanges: { current: 156, previous: 142, target: 150 }
  };

  const auditLogs = [
    {
      id: 1,
      timestamp: '2024-01-10 14:32:15',
      user: 'john.smith@company.com',
      userId: 'EMP001',
      action: 'LOGIN_SUCCESS',
      category: 'Authentication',
      severity: 'info',
      resource: 'HRMS Portal',
      resourceId: null,
      ipAddress: '192.168.1.100',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
      location: 'Mumbai, India',
      details: 'User successfully logged into HRMS portal',
      changes: null,
      sessionId: 'sess_abc123',
      deviceType: 'Desktop',
      riskScore: 'low'
    },
    {
      id: 2,
      timestamp: '2024-01-10 14:28:42',
      user: 'sarah.johnson@company.com',
      userId: 'EMP002',
      action: 'EMPLOYEE_UPDATE',
      category: 'Data Modification',
      severity: 'medium',
      resource: 'Profile',
      resourceId: 'EMP045',
      ipAddress: '192.168.1.101',
      userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36',
      location: 'Delhi, India',
      details: 'Updated employee salary information',
      changes: {
        field: 'salary',
        oldValue: '₹50,000',
        newValue: '₹55,000',
        reason: 'Annual increment'
      },
      sessionId: 'sess_def456',
      deviceType: 'Desktop',
      riskScore: 'medium'
    },
    {
      id: 3,
      timestamp: '2024-01-10 14:25:18',
      user: 'mike.davis@company.com',
      userId: 'EMP003',
      action: 'POLICY_DOWNLOAD',
      category: 'Document Access',
      severity: 'info',
      resource: 'Policy Document',
      resourceId: 'POL001',
      ipAddress: '192.168.1.102',
      userAgent: 'Mozilla/5.0 (iPhone; CPU iPhone OS 15_0 like Mac OS X) AppleWebKit/605.1.15',
      location: 'Bangalore, India',
      details: 'Downloaded Employee Code of Conduct policy',
      changes: null,
      sessionId: 'sess_ghi789',
      deviceType: 'Mobile',
      riskScore: 'low'
    },
    {
      id: 4,
      timestamp: '2024-01-10 14:20:33',
      user: 'admin@company.com',
      userId: 'ADM001',
      action: 'USER_ROLE_CHANGE',
      category: 'Access Control',
      severity: 'high',
      resource: 'User Role',
      resourceId: 'EMP025',
      ipAddress: '192.168.1.103',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
      location: 'Mumbai, India',
      details: 'Changed user role from Employee to Manager',
      changes: {
        field: 'role',
        oldValue: 'Employee',
        newValue: 'Manager',
        reason: 'Promotion'
      },
      sessionId: 'sess_jkl012',
      deviceType: 'Desktop',
      riskScore: 'high'
    },
    {
      id: 5,
      timestamp: '2024-01-10 14:15:07',
      user: 'unknown@external.com',
      userId: null,
      action: 'LOGIN_FAILED',
      category: 'Authentication',
      severity: 'warning',
      resource: 'HRMS Portal',
      resourceId: null,
      ipAddress: '203.192.45.67',
      userAgent: 'Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36',
      location: 'Unknown',
      details: 'Failed login attempt with invalid credentials',
      changes: null,
      sessionId: null,
      deviceType: 'Desktop',
      riskScore: 'high'
    },
    {
      id: 6,
      timestamp: '2024-01-10 14:10:22',
      user: 'hr.manager@company.com',
      userId: 'EMP004',
      action: 'REPORT_EXPORT',
      category: 'Data Export',
      severity: 'medium',
      resource: 'Payroll Report',
      resourceId: 'RPT001',
      ipAddress: '192.168.1.104',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
      location: 'Mumbai, India',
      details: 'Exported monthly payroll report to Excel',
      changes: {
        exportFormat: 'Excel',
        recordCount: 150,
        dateRange: '2024-01-01 to 2024-01-31'
      },
      sessionId: 'sess_mno345',
      deviceType: 'Desktop',
      riskScore: 'medium'
    },
    {
      id: 7,
      timestamp: '2024-01-10 14:05:45',
      user: 'system@company.com',
      userId: 'SYS001',
      action: 'BACKUP_COMPLETED',
      category: 'System',
      severity: 'info',
      resource: 'Database',
      resourceId: 'DB_HRMS',
      // IP address will be captured by backend from request
      userAgent: 'System Process',
      location: 'Server',
      details: 'Automated database backup completed successfully',
      changes: {
        backupSize: '2.4 GB',
        duration: '15 minutes',
        status: 'Success'
      },
      sessionId: null,
      deviceType: 'Server',
      riskScore: 'low'
    },
    {
      id: 8,
      timestamp: '2024-01-10 14:00:12',
      user: 'finance.head@company.com',
      userId: 'EMP005',
      action: 'SALARY_APPROVAL',
      category: 'Workflow',
      severity: 'high',
      resource: 'Salary Increment',
      resourceId: 'INC001',
      ipAddress: '192.168.1.105',
      userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36',
      location: 'Mumbai, India',
      details: 'Approved salary increment for multiple employees',
      changes: {
        approvedCount: 25,
        totalAmount: '₹2,50,000',
        effectiveDate: '2024-02-01'
      },
      sessionId: 'sess_pqr678',
      deviceType: 'Desktop',
      riskScore: 'medium'
    }
  ];

  // Prefer real backend data
  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const resp = await auditLogService.getAuditLogs({ page: currentPage, limit: itemsPerPage });
        const payload = resp?.data || resp;
        const logs = payload?.logs || payload?.items || [];
        const mapped = (logs || []).map(l => ({
          id: l.id || l._id,
          timestamp: l.timestamp || l.formattedTimestamp,
          user: l.user?.name || l.userName || 'Unknown',
          userId: l.userId || '',
          action: l.action,
          category: l.module || 'General',
          severity: String(l.riskLevel || 'LOW').toLowerCase(),
          resource: l.resourceName || l.resourceType || '',
          resourceId: l.resourceId || '',
          ipAddress: l.ipAddress || '',
          userAgent: l.userAgent || '',
          location: l.location || '',
          details: l.description || '',
          changes: l.details || null,
          sessionId: l.sessionId || null,
          deviceType: l.deviceType || 'Device'
        }));
        setServerLogs(mapped);
        setTotalCount(payload?.totalCount || mapped.length || 0);
      } catch (e) {
        setServerLogs([]);
        setTotalCount(0);
      }
    };
    fetchLogs();
  }, [currentPage, itemsPerPage]);

  const baseLogs = serverLogs.length ? serverLogs : auditLogs;

  const activitySummary = [
    {
      category: 'Authentication',
      total: 1247,
      success: 1224,
      failed: 23,
      percentage: 98.2
    },
    {
      category: 'Data Modification',
      total: 456,
      success: 451,
      failed: 5,
      percentage: 98.9
    },
    {
      category: 'Document Access',
      total: 789,
      success: 789,
      failed: 0,
      percentage: 100.0
    },
    {
      category: 'Access Control',
      total: 89,
      success: 87,
      failed: 2,
      percentage: 97.8
    },
    {
      category: 'Data Export',
      total: 156,
      success: 154,
      failed: 2,
      percentage: 98.7
    },
    {
      category: 'System',
      total: 110,
      success: 108,
      failed: 2,
      percentage: 98.2
    }
  ];

  const riskAnalysis = [
    {
      riskLevel: 'Critical',
      count: 5,
      percentage: 0.2,
      trend: 'down',
      examples: ['Multiple failed login attempts', 'Unauthorized access attempts']
    },
    {
      riskLevel: 'High',
      count: 23,
      percentage: 0.8,
      trend: 'stable',
      examples: ['Role changes', 'Sensitive data access']
    },
    {
      riskLevel: 'Medium',
      count: 187,
      percentage: 6.6,
      trend: 'up',
      examples: ['Data exports', 'Profile updates']
    },
    {
      riskLevel: 'Low',
      count: 2632,
      percentage: 92.4,
      trend: 'stable',
      examples: ['Normal logins', 'Document views']
    }
  ];

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-red-600 bg-red-50 border-red-200';
      case 'warning': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'info': return 'text-[#0F2942] bg-blue-50 border-blue-200';
      case 'low': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'critical': return 'text-red-600 bg-red-50';
      case 'high': return 'text-red-600 bg-red-50';
      case 'medium': return 'text-yellow-600 bg-yellow-50';
      case 'low': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'LOGIN_SUCCESS': return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'LOGIN_FAILED': return <XCircle className="h-4 w-4 text-red-600" />;
      case 'EMPLOYEE_UPDATE': return <Edit className="h-4 w-4 text-[#0F2942]" />;
      case 'POLICY_DOWNLOAD': return <Download className="h-4 w-4 text-purple-600" />;
      case 'USER_ROLE_CHANGE': return <Shield className="h-4 w-4 text-orange-600" />;
      case 'REPORT_EXPORT': return <FileText className="h-4 w-4 text-indigo-600" />;
      case 'BACKUP_COMPLETED': return <Database className="h-4 w-4 text-green-600" />;
      case 'SALARY_APPROVAL': return <CheckCircle className="h-4 w-4 text-green-600" />;
      default: return <Activity className="h-4 w-4 text-gray-600" />;
    }
  };

  const filteredLogs = baseLogs.filter(log => {
    const categoryMatch = selectedCategory === 'all' || log.category.toLowerCase().includes(selectedCategory.toLowerCase());
    const severityMatch = selectedSeverity === 'all' || log.severity === selectedSeverity;
    const userMatch = selectedUser === 'all' || log.user.includes(selectedUser);
    const searchMatch = searchTerm === '' ||
      log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.details.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.user.toLowerCase().includes(searchTerm.toLowerCase());
    return categoryMatch && severityMatch && userMatch && searchMatch;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredLogs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedLogs = filteredLogs.slice(startIndex, endIndex);

  // Pagination handlers
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, selectedSeverity, selectedUser, searchTerm]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Audit Logs</h2>
          <p className="text-sm text-gray-600">Monitor system activities and security events</p>
        </div>

        <div className="flex items-center space-x-4">
          <select
            value={viewType}
            onChange={(e) => setViewType(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="logs">Audit Logs</option>
            <option value="analytics">Analytics</option>
            <option value="reports">Reports</option>
          </select>

          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="1day">Last 24 Hours</option>
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="90days">Last 90 Days</option>
          </select>

          <Button
            variant="outline"
            onClick={() => {
              // Export all audit logs
              const csvContent = [
                ['Timestamp', 'User', 'Action', 'Module', 'Status', 'Details'],
                ...auditLogs.map(log => [
                  log.timestamp,
                  log.user,
                  log.action,
                  log.module,
                  log.status,
                  JSON.stringify(log.details)
                ])
              ].map(row => row.join(',')).join('\n');

              const blob = new Blob([csvContent], { type: 'text/csv' });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `audit_logs_export_${new Date().toISOString().split('T')[0]}.csv`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              window.URL.revokeObjectURL(url);
            }}
            className="flex items-center space-x-2"
          >
            <Download className="h-4 w-4" />
            <span>Export</span>
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Events</p>
              <p className="text-2xl font-bold text-gray-900">{formatIndianNumber(auditMetrics.totalEvents.current)}</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-full">
              <Activity className="h-6 w-6 text-[#0F2942]" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <TrendingUp className="h-4 w-4 text-green-500" />
            <span className="text-sm text-green-600 ml-1">
              +{auditMetrics.totalEvents.current - auditMetrics.totalEvents.previous}
            </span>
            <span className="text-sm text-gray-500 ml-2">this week</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Critical Events</p>
              <p className="text-2xl font-bold text-gray-900">{formatIndianNumber(auditMetrics.criticalEvents.current)}</p>
            </div>
            <div className="p-3 bg-red-50 rounded-full">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <TrendingUp className="h-4 w-4 text-green-500 transform rotate-180" />
            <span className="text-sm text-green-600 ml-1">
              -{auditMetrics.criticalEvents.previous - auditMetrics.criticalEvents.current}
            </span>
            <span className="text-sm text-gray-500 ml-2">improvement</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Failed Logins</p>
              <p className="text-2xl font-bold text-gray-900">{formatIndianNumber(auditMetrics.failedLogins.current)}</p>
            </div>
            <div className="p-3 bg-yellow-50 rounded-full">
              <Lock className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <TrendingUp className="h-4 w-4 text-green-500 transform rotate-180" />
            <span className="text-sm text-green-600 ml-1">
              -{auditMetrics.failedLogins.previous - auditMetrics.failedLogins.current}
            </span>
            <span className="text-sm text-gray-500 ml-2">vs last week</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Data Changes</p>
              <p className="text-2xl font-bold text-gray-900">{formatIndianNumber(auditMetrics.dataChanges.current)}</p>
            </div>
            <div className="p-3 bg-green-50 rounded-full">
              <Edit className="h-6 w-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <TrendingUp className="h-4 w-4 text-green-500" />
            <span className="text-sm text-green-600 ml-1">
              +{auditMetrics.dataChanges.current - auditMetrics.dataChanges.previous}
            </span>
            <span className="text-sm text-gray-500 ml-2">this week</span>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search logs by action, user, or details..."
                value={searchTerm}
                onChange={(e) => {
                  // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                  const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                  setSearchTerm(value);
                }}
                onKeyPress={(e) => {
                  // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                  if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                maxLength={100}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0F2942]/50 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="all">All Categories</option>
              <option value="authentication">Authentication</option>
              <option value="data modification">Data Modification</option>
              <option value="document access">Document Access</option>
              <option value="access control">Access Control</option>
              <option value="data export">Data Export</option>
              <option value="system">System</option>
            </select>

            <select
              value={selectedSeverity}
              onChange={(e) => setSelectedSeverity(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="all">All Severity</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="warning">Warning</option>
              <option value="medium">Medium</option>
              <option value="info">Info</option>
              <option value="low">Low</option>
            </select>

            <button className="text-gray-400 hover:text-gray-600">
              <RefreshCw className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Audit Logs Table */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">{filteredLogs.length} events</span>
            <button className="text-gray-400 hover:text-gray-600">
              <Filter className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Debug Information */}
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="text-sm text-yellow-800">
            <strong>Debug Info:</strong> Page {currentPage} of {totalPages} |
            Items per page: {itemsPerPage} |
            Showing {paginatedLogs.length} items |
            Total filtered: {filteredLogs.length} |
            Total logs: {auditLogs.length}
          </div>
        </div>

        <div className="space-y-3">
          {paginatedLogs.map((log) => (
            <div key={log.id} className={`border rounded-lg p-4 hover:shadow-md transition-shadow ${getSeverityColor(log.severity)}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1">
                  <div className="mt-1">
                    {getActionIcon(log.action)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h4 className="text-sm font-semibold text-gray-900">{log.action.replace('_', ' ')}</h4>
                      <span className={`px-2 py-1 text-xs rounded-full border ${getSeverityColor(log.severity)}`}>
                        {log.severity.toUpperCase()}
                      </span>
                      <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                        {log.category}
                      </span>
                      <span className={`px-2 py-1 text-xs rounded-full ${getRiskColor(log.riskScore)}`}>
                        {log.riskScore} risk
                      </span>
                    </div>

                    <p className="text-sm text-gray-600 mb-3">{log.details}</p>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
                      <div>
                        <p className="text-gray-500">User</p>
                        <p className="font-medium text-gray-900">{log.user}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Timestamp</p>
                        <p className="font-medium text-gray-900">{log.timestamp}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">IP Address</p>
                        <p className="font-medium text-gray-900">{log.ipAddress}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Device</p>
                        <p className="font-medium text-gray-900">{log.deviceType}</p>
                      </div>
                    </div>

                    {log.changes && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-md">
                        <p className="text-xs font-medium text-gray-700 mb-1">Changes:</p>
                        <div className="text-xs text-gray-600">
                          {typeof log.changes === 'object' ? (
                            Object.entries(log.changes).map(([key, value]) => (
                              <div key={key} className="flex justify-between">
                                <span className="font-medium">{key}:</span>
                                <span>{typeof value === 'object' ? JSON.stringify(value) : value}</span>
                              </div>
                            ))
                          ) : (
                            <span>{log.changes}</span>
                          )}
                        </div>
                      </div>
                    )}

                    {log.location && log.location !== 'Server' && (
                      <div className="mt-2 text-xs text-gray-500">
                        Location: {log.location}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-2 ml-4">
                  <button
                    className="p-2 text-gray-400 hover:text-gray-600"
                    title="View Details"
                    onClick={() => navigate(`/audit_log_detail/${log.id}`)}
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button
                    className="p-2 text-gray-400 hover:text-gray-600"
                    title="Export"
                    onClick={() => {
                      // Export individual log
                      const logData = {
                        id: log.id,
                        timestamp: log.timestamp,
                        user: log.user,
                        action: log.action,
                        module: log.module,
                        status: log.status,
                        details: log.details
                      };

                      const blob = new Blob([JSON.stringify(logData, null, 2)], { type: 'application/json' });
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `audit_log_${log.id}_${new Date().toISOString().split('T')[0]}.json`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      window.URL.revokeObjectURL(url);
                    }}
                  >
                    <Download className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex items-center justify-between">
          <span className="text-sm text-gray-600">
            Showing {startIndex + 1} to {Math.min(endIndex, filteredLogs.length)} of {filteredLogs.length} events
            {filteredLogs.length !== auditLogs.length && ` (filtered from ${auditLogs.length} total)`}
          </span>
          <div className="flex items-center space-x-2">
            <button
              className={`px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
            >
              Previous
            </button>

            {/* Page numbers */}
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    className={`px-3 py-1 text-sm border rounded-md ${currentPage === pageNum
                      ? 'bg-[#0F2942] text-white border-[#0F2942]'
                      : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              className={`px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Activity Summary & Risk Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Activity Summary */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Activity Summary</h3>
            <button className="text-gray-400 hover:text-gray-600">
              <BarChart3 className="h-5 w-5" />
            </button>
          </div>
          <div className="space-y-4">
            {activitySummary.map((activity, index) => (
              <div key={index} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-gray-900">{activity.category}</h4>
                  <span className={`px-2 py-1 text-xs rounded-full ${activity.percentage >= 98 ? 'text-green-600 bg-green-50' :
                    activity.percentage >= 95 ? 'text-yellow-600 bg-yellow-50' :
                      'text-red-600 bg-red-50'
                    }`}>
                    {activity.percentage}%
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="text-center">
                    <p className="text-gray-500">Total</p>
                    <p className="font-semibold text-gray-900">{activity.total}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-500">Success</p>
                    <p className="font-semibold text-green-600">{activity.success}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-500">Failed</p>
                    <p className="font-semibold text-red-600">{activity.failed}</p>
                  </div>
                </div>
                <div className="mt-2">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${activity.percentage >= 98 ? 'bg-green-500' :
                        activity.percentage >= 95 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                      style={{ width: `${activity.percentage}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Risk Analysis */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Risk Analysis</h3>
            <button className="text-gray-400 hover:text-gray-600">
              <PieChart className="h-5 w-5" />
            </button>
          </div>
          <div className="space-y-4">
            {riskAnalysis.map((risk, index) => (
              <div key={index} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-gray-900">{risk.riskLevel} Risk</h4>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${getRiskColor(risk.riskLevel.toLowerCase())}`}>
                      {risk.count} events
                    </span>
                    <span className="text-xs text-gray-600">{risk.percentage}%</span>
                  </div>
                </div>
                <div className="text-xs text-gray-600 mb-2">
                  Examples: {risk.examples.join(', ')}
                </div>
                <div className="flex items-center justify-between">
                  <div className="w-full bg-gray-200 rounded-full h-2 mr-2">
                    <div
                      className={`h-2 rounded-full ${risk.riskLevel === 'Critical' ? 'bg-red-500' :
                        risk.riskLevel === 'High' ? 'bg-red-400' :
                          risk.riskLevel === 'Medium' ? 'bg-yellow-500' : 'bg-green-500'
                        }`}
                      style={{ width: `${risk.percentage}%` }}
                    ></div>
                  </div>
                  <span className={`text-xs ${risk.trend === 'up' ? 'text-red-600' :
                    risk.trend === 'down' ? 'text-green-600' : 'text-gray-600'
                    }`}>
                    {risk.trend === 'up' ? '↗' : risk.trend === 'down' ? '↘' : '→'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditLogs;